import React, { useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import OtpInput from 'react-otp-input';
import LoginBg from '../../assets/images/Login-bg.svg';
import { toast } from 'react-toastify';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import Loader from '../../components/Loader/Loader';
import BrandLogo from '../../components/BrandLogo/BrandLogo';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loader, setLoader] = useState(false);
  const [otp, setOtp] = useState('');
  const [count, setCount] = useState(120);
  const [focusedIndex, setFocusedIndex] = useState(null);
  const [resendEnabled, setResendEnabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    mobile: '',
    password: '',
    confirmPassword: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile') {
      // Only allow numbers and limit to 10 digits
      const mobileValue = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({
        ...prev,
        [name]: mobileValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    setErrorMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.mobile.length !== 10) {
      setErrorMessage("Please enter a valid 10-digit mobile number");
      return;
    }
    try {
      setLoader(true);
      // Mock API call - simulate delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      setLoader(false);
      setStep(2);
      setCount(120);
      setResendEnabled(false);
      toast.success('OTP sent successfully!');
    } catch (e) {
      setLoader(false);
      toast.error('Failed to send OTP');
    }
  };

  const verifyOtp = async () => {
    // For demo, accept any 4-digit OTP
    if (otp.length === 4) {
      try {
        setLoader(true);
        // Mock API call - simulate delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        setLoader(false);
        setStep(3);
        setOtp(''); // Clear OTP when moving to next step
        toast.success('OTP verified successfully!');
      } catch (e) {
        setLoader(false);
        toast.error('Failed to verify OTP');
      }
    } else {
      setErrorMessage("Please enter correct OTP.");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setErrorMessage("Passwords do not match");
      return;
    }
    if (formData.password.length < 6) {
      setErrorMessage("Password must be at least 6 characters long");
      return;
    }
    try {
      setLoader(true);
      // Mock API call - simulate delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      setLoader(false);
      toast.success("Password reset successfully!");
      navigate('/login');
    } catch (e) {
      setLoader(false);
      toast.error('Failed to reset password');
    }
  };

  const handleResendOtp = async () => {
    try {
      setLoader(true);
      // Mock API call - simulate delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      setCount(120);
      setResendEnabled(false);
      setLoader(false);
      toast.success('OTP resent successfully!');
    } catch (e) {
      setLoader(false);
      toast.error('Failed to resend OTP');
    }
  };

  React.useEffect(() => {
    if (step === 2 && count > 0) {
      const timer = setTimeout(() => setCount(prevCount => prevCount - 1), 1000);
      return () => clearTimeout(timer);
    } else if (count === 0) {
      setResendEnabled(true);
    }
  }, [count, step]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <div className="min-vh-100 d-flex">
      {/* Left Section - Hero Image */}
      <div className="flex-grow-1 d-none d-lg-block position-relative"
        style={{
          background: 'radial-gradient(circle at top center, #d4b5f9 10%, #fdfbff 67%)'
        }}>
        <div style={{
          position: 'absolute',
          width: '66%',
          height: '50%',
          top: '15%',
          left: '10%',
          backgroundImage: `url(${LoginBg})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }} />
      </div>

      {/* Right Section - Form */}
      <div className="d-flex align-items-center bg-white justify-content-center p-4" style={{ width: '456px' }}>
        <div className="w-100" style={{ maxWidth: '360px' }}>
                  <div className="text-center mb-4">
                      <div className="d-flex align-items-center justify-content-left gap-2">
                          <BrandLogo height={32} />
                      </div>
                  </div>

          {step === 1 && (
            <>
              <h5 className="text-left mb-4">Forgot Password</h5>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-4">
                  <Form.Label className="small text-dark">Registered Mobile Number</Form.Label>
                  <Form.Control
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter Mobile Number"
                    className="py-2"
                    required
                  />
                  {errorMessage && (
                    <div className="text-danger small mt-1">{errorMessage}</div>
                  )}
                </Form.Group>

                <Button
                  type="submit"
                  variant="danger"
                  className="w-100 py-2 mb-4 btn-custom-danger"
                  disabled={loader}
                >
                  {loader ? <Loader style={{ width: 40, height: 20 }} /> : "Reset Password "}
                </Button>

                <div className="text-center small">
                  <Button
                    variant="link"
                    className="text-secondary text-decoration-none p-0"
                    onClick={() => navigate('/login')}
                  >
                    Back to <span className="text-primary">Login page</span>
                  </Button>
                </div>
              </Form>
            </>
          )}

          {step === 2 && (
           <>
           <h5 className="text-left mb-4">Reset Password</h5>
           <div className="mb-4 small text-dark">
             OTP sent to your registered mobile number{' '}
             <strong>+91 {formData.mobile}</strong>
           </div>
         
           <div className="d-flex justify-content-center mb-4">
      <OtpInput
        value={otp}
        onChange={setOtp}
        numInputs={4}
        renderSeparator={<span>&nbsp;</span>}
        renderInput={(props, index) => (
          <input
            {...props}
            inputMode="numeric"
            pattern="[0-9]*"
            onFocus={() => setFocusedIndex(index)}
            onBlur={() => setFocusedIndex(null)}
            onKeyPress={(e) => {
              if (!/[0-9]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            style={{
              width: '45px',
              height: '45px',
              margin: '0 8px',
              fontSize: '1.2rem',
              borderRadius: '4px',
              textAlign: 'center',
              border: focusedIndex === index ? '0.5px solid #C9194A' : '1px solid #ccc',
              backgroundColor: focusedIndex === index ? '#FFF6F9' : '#fff',
              outline: 'none',
            }}
          />
        )}
        containerStyle={{ justifyContent: 'center' }}
      />
    </div>
  
         
           {errorMessage && (
             <div className="text-center text-danger small mb-3">{errorMessage}</div>
           )}
         
           {/* Moved Resend OTP section here */}
           <div className="text-left mb-3">
             {resendEnabled ? (
               <div
                 className="text-primary small"
                 style={{ cursor: 'pointer' }}
                 onClick={handleResendOtp}
               >
                 <u>Resend OTP</u>
               </div>
             ) : (
               <div className="text-secondary small">
                 Resend OTP in <span className="text-primary">{formatTime(count)}</span>
               </div>
             )}
           </div>
         
           <Button
             variant="danger"
             className="w-100 py-2 mb-3 btn-custom-danger"
             onClick={verifyOtp}
             disabled={loader || otp.length !== 4}
           >
             {loader ? <Loader style={{ width: 40, height: 20 }} /> : "Continue"}
           </Button>
         
           <div className="text-center small">
             <Button
               variant="link"
               className="text-secondary text-decoration-none p-0"
               onClick={() => navigate('/login')}
             >
               Back to <span className="text-primary">Login page</span>
             </Button>
           </div>
         </>
          )}

          {step === 3 && (
            <>
              <h5 className="text-left mb-4">Reset Password</h5>
              <Form onSubmit={handleResetPassword}>
                <Form.Group className="mb-3">
                  <Form.Label className="small text-dark">New Password</Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter new password"
                      className="py-2"
                      required
                    />
                    <Button
                      variant="link"
                      className="position-absolute end-0 top-50 translate-middle-y text-secondary pe-2"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ zIndex: 10 }}
                    >
                      {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                    </Button>
                  </div>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="small text-dark">Confirm Password</Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm new password"
                      className="py-2"
                      required
                    />
                    <Button
                      variant="link"
                      className="position-absolute end-0 top-50 translate-middle-y text-secondary pe-2"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{ zIndex: 10 }}
                    >
                      {showConfirmPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                    </Button>
                  </div>
                </Form.Group>

                {errorMessage && (
                  <div className="text-center text-danger small mb-3">{errorMessage}</div>
                )}

                <Button
                  type="submit"
                  variant="danger"
                  className="w-100 py-2 mb-4 btn-custom-danger"
                  disabled={loader}
                >
                  {loader ? <Loader style={{ width: 40, height: 20 }} /> : "Submit"}
                </Button>

                <div className="text-center small">
                  <Button
                    variant="link"
                    className="text-secondary text-decoration-none p-0"
                    onClick={() => navigate('/login')}
                  >
                     Back to <span className="text-primary">Login page</span>
                  </Button>
                </div>
              </Form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword; 